import React, {
  forwardRef,
  useState,
  useMemo,
  useCallback,
  useRef,
} from "react";
import ToolsIntegrationsAPI from "restAPI/ToolsIntegrationAPI";
import { FieldTypes, generateFormFields, parseSelectOptions } from "utils/formUtils";
import GenericMappingForm from "../GenericMappingForm";
import { Form, Button } from "antd";
import { uniqBy, find, isEmpty, map, forEach, isString, compact, includes, split, some, filter } from "lodash";

const INGESTION_TYPE_OPTIONS = ["Vulnerabilities", "Host Audit"];
const HOST_AUDIT_RESULT_OPTIONS = ["PASSED", "FAILED", "WARNING", "SKIPPED", "ERROR", "UNKNOWN"];

const initialFormValues = {
  product: undefined,
  subProduct: undefined,
  environment: undefined,
  projectList: [{ tagKey: undefined }],
  ingestionType: ["Vulnerabilities"],
  hostAuditResult: undefined,
};

function createTagObjects(assetTagKey, assetTagUuid, assetTagValue) {
  const tagKeys = split(assetTagKey, ",");
  const tagUuids = split(assetTagUuid, ";");
  const tagValues = split(assetTagValue, ";");

  return tagKeys.map((name, key) => {
    const [uuidPart, valuePart] = split(tagUuids[key], ":");
    const valuesArray = split(tagValues[key], ",");

    return {
      id: uuidPart,
      name,
      otherProperties: {
        assetTagValues: valuesArray.map((valName, valKey) => ({
          id: split(valuePart, ",")[valKey],
          name: valName
        }))
      }
    };
  });
}

function serializeMultiSelect(values, allOptionsCount) {
  if (isEmpty(values) || values.length === allOptionsCount) return "All";
  return values.join(",");
}

function deserializeMultiSelect(value, allOptions) {
  if (value === "All") return allOptions; 
  if (!value) return undefined; 
  // Otherwise, split the comma-separated string from the backend
  return split(value, ",");
}

const TenableVulnerabilityManagementMappingFormV2 = forwardRef(
  function TenableVulnerabilityManagementMappingFormV2(props, ref) {

    const [mappedKeyList] = useState(() => {
      if (props.record) {
        const { assetTagKey, assetTagUuid, assetTagValue } = props.record;
        if(!assetTagKey && !assetTagValue){
          return { keys: [] }
        }
        return {
          keys: createTagObjects(assetTagKey, assetTagUuid, assetTagValue),
        };
      }
      return { keys: [] };
    });

    const tagKeyRef = useRef({ keys: mappedKeyList.keys });
    // tagKeysVersion is needed to trigger a re-render when tagKeyRef is updated,
    // since useRef updates don't cause React re-renders. Without this, the Asset Tag Value
    // MULTI_SELECT options won't reflect the full data fetched by the API on mount.
    const [tagKeysVersion, setTagKeysVersion] = useState(0);

    const [form] = Form.useForm();

    const [initialValues] = useState(() => {
      if (props.record) {
        return [];
      }
      return [
        {
          product: props.defaultProduct,
          subProduct: props.defaultSubProduct,
          subProductId: props.defaultSubProductId,
          environment: props.defaultEnvironment,
          ...initialFormValues,
        },
      ];
    });

    const fetchAssetsKeyAndValue = useCallback(
      (params) => {
        const { promise, cancel } =
          ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, {
            ...params,
            type: "tag_key",
            login_id: props.loginConfigId,
          });

        promise.then((response) => {
          const { projects } = response;
          if (mappedKeyList["keys"].length > 0 && !isEmpty(projects)) {
            response.projects = uniqBy(
              [...projects, ...mappedKeyList["keys"]],
              "id"
            );
          }
          tagKeyRef.current["keys"] = uniqBy([...response.projects,...tagKeyRef.current["keys"]],"id");
          setTagKeysVersion(v => v + 1);
        });

        return {
          promise,
          cancel,
        };
      },
      [props.toolName, mappedKeyList, props.loginConfigId]
    );

    const parseExtraFieldsDataFromResponse = useCallback((config) => {
      const { assetTagUuid, assetTagKey, assetTagValue, ingestionType, hostAuditResult } = config;

      const projectList = (!assetTagKey && !assetTagValue)
        ? [{ tagKey: undefined, tagValues: undefined }]
        : split(assetTagUuid, ";").map(pair => {
            const tagKey = split(pair, ":")[0];
            return {
              tagKey,
              tagValues: split(pair, ":")[1]?.split(",")
            }
          });

      return {
        projectList,
        // Map "All" back to ["Vulnerabilities", "Host Audit"]
        ingestionType: deserializeMultiSelect(
          ingestionType, 
          INGESTION_TYPE_OPTIONS, 
        ),
        // Map "All" back to the full list of Audit Results
        hostAuditResult: deserializeMultiSelect(
          hostAuditResult, 
          HOST_AUDIT_RESULT_OPTIONS, 
        )
      };
    }, []);

    const getExtraFieldsData = (config) => {
      const { projectList, ingestionType, hostAuditResult } = config;

      // Serialize ingestionType
      const serializedIngestionType = serializeMultiSelect(ingestionType, INGESTION_TYPE_OPTIONS.length);

      // Serialize hostAuditResult — only if Host Audit is selected
      const hasHostAudit = includes(ingestionType, "Host Audit") || serializedIngestionType === "All";
      const serializedHostAuditResult = hasHostAudit
        ? serializeMultiSelect(hostAuditResult, HOST_AUDIT_RESULT_OPTIONS.length)
        : null;

      // Asset tag fields
      if(!some(projectList, (config) => config.tagKey)){
        return {
          assetTagKey: null,
          assetTagValue: null,
          assetTagUuid: null,
          ingestionType: serializedIngestionType,
          hostAuditResult: serializedHostAuditResult,
        }
      }
      const filteredProjectList = filter(projectList, (config => config.tagKey));
      const tagKeyNamesArr = filteredProjectList.reduce((acc, cv) => {
        const { tagKey } = cv;
        const tagKeyName = find(tagKeyRef.current.keys, (i) => i.id === tagKey)?.name
        acc.push(tagKeyName)
        return acc;
      }, []);
      const tagsUuid = filteredProjectList.reduce((acc, cv) => {
        const { tagKey, tagValues } = cv;
        const tagValueOptions = find(
          tagKeyRef.current.keys,
          (i) => i.id === tagKey
        )?.otherProperties?.assetTagValues;
        if(isEmpty(tagValues)){
          acc.push(`${tagKey}:${map(tagValueOptions, i => i.id).join()}`);
          return acc;
        }
        acc.push(`${tagKey}:${tagValues}`)
        return acc;
      }, []);
      const tagValueNames =  filteredProjectList.map(config => {
        const tagValueOptions = find(
          tagKeyRef.current.keys,
          (i) => i.id === config.tagKey
        )?.otherProperties?.assetTagValues;
        if(isEmpty(config.tagValues)){
          //if no asset tag value is selected
          //sending all asset tag values
          const tagValueNames = map(tagValueOptions, value => value?.name)
          return tagValueNames.join(",")
        }
        const tagValueNames = config.tagValues.map(t => find(tagValueOptions, (i) => i.id === t)?.name)
        return tagValueNames.join(",")
      }).join(";");
      
      return {
        assetTagKey: tagKeyNamesArr.join(),
        assetTagValue: tagValueNames,
        assetTagUuid: tagsUuid.join(";"),
        ingestionType: serializedIngestionType,
        hostAuditResult: serializedHostAuditResult,
      };
    };

    const finalKeyOptions = useCallback(
      (projects, listField) => {
        const selected = compact(
          map(
            form.getFieldsValue().configurations?.[listField.name]?.projectList,
            (p) => p?.tagKey
          )
        );
        return map(projects, (k) => ({
          ...k,
          disabled: includes(selected, k.id),
        }));
      },
      [form]
    );

    const projectListRender = useCallback(
      (listField) => {
        const __fields = [
          {
            name: "tagKey",
            colSpan: 10,
            type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
            placeholder: "Leave blank to fetch all Findings",
            label: "Asset Tag Key",
            props: {
              autoFocus: true,
              api: fetchAssetsKeyAndValue,
              optionsMutator: (options) => finalKeyOptions(options, listField),
            },
          },
          {
            name: "tagValues",
            colSpan: 10,
            type: FieldTypes.MULTI_SELECT,
            placeholder: "Leave blank to fetch all Findings",
            label: "Asset Tag Value",
            props: {
              setExtraProps: (field, nestedListField) => {
                const configs = form.getFieldValue("configurations");
                const tagKey = configs[listField.name]?.projectList[nestedListField.name]
                ?.tagKey;
                const assetTagValuesOptions = find(
                  tagKeyRef.current.keys,
                  (i) => i.id === tagKey
                )?.otherProperties?.assetTagValues;
                return {
                  options: parseSelectOptions(assetTagValuesOptions),
                  resetOptionsProp: tagKey,
                  loading: tagKey && tagKeysVersion === 0,
                };
              },
            },
          },
          {
            name: "add",
            colSpan: 2,
            render: ({ remove, name, fields }) => {
              return (
                <Button
                  disabled={fields.length === 1}
                  type="dashed"
                  onClick={() => {
                    remove(name);
                  }}
                  className="m-t-l"
                >
                  -
                </Button>
              );
            },
          },
        ];
  
        return (
          <div className="m-b-sm">
            <Form.List name={[listField.name, "projectList"]}>
              {(fields, { add, remove }) => {
                return (
                  <div>
                    {map(fields, (field) => {
                      return generateFormFields(
                        __fields,
                        10,
                        { ...field, add, remove, fields },
                        field.name
                      );
                    })}
                    <Button
                      size="small"
                      type="dashed"
                      className="m-t-l"
                      onClick={() => add({ tagKey: undefined })}
                    >
                      + Add Asset Tag Key/Value
                    </Button>
                  </div>
                );
              }}
            </Form.List>
          </div>
        );
      },
      [form, fetchAssetsKeyAndValue, finalKeyOptions, tagKeysVersion]
    );

    const ingestionFieldsRender = useCallback(
      (listField) => {
        const selectedIngestionType = form.getFieldsValue().configurations?.[listField.name]?.ingestionType;
        const hasHostAudit = includes(selectedIngestionType, "Host Audit") || isEmpty(selectedIngestionType);

        const fields = [
          ...(hasHostAudit ? [{
            name: "hostAuditResult",
            colSpan: 12,
            type: FieldTypes.MULTI_SELECT,
            label: "Select Host Audit Ingestion Scope",
            placeholder: "Leave blank to select all",
            options: HOST_AUDIT_RESULT_OPTIONS,
          }] : []),
        ];

        return fields.length > 0
          ? <div className="m-b-sm">{generateFormFields(fields, 12, listField)}</div>
          : null;
      },
      [form]
    );

    const extraFields = useMemo(
      () => [
        {
          name: "ingestionType",
          colSpan: 12,
          type: FieldTypes.MULTI_SELECT,
          label: "Select Finding Ingestion Scope",
          placeholder: "Leave blank to select all",
          options: INGESTION_TYPE_OPTIONS,
        },
        {
          name: (listField) => [listField.name, "hostAuditResult"],
          render: ingestionFieldsRender,
        },
        {
          name: (listField) => [listField.name, "projectList"],
          render: projectListRender,
        },
      ],
      [projectListRender, ingestionFieldsRender]
    );

    const onValuesChange = useCallback(
      (values) => {
        if (values.configurations) {
          let isChanged = false;
          const formValues = form.getFieldsValue();
          forEach(values.configurations, (configs, index) => {
            if (!configs) return;

            // Clear hostAuditResult when Host Audit is deselected from ingestionType
            if (configs.ingestionType !== undefined) {
              const hasHostAudit = includes(configs.ingestionType, "Host Audit") || isEmpty(configs.ingestionType);
              if (!hasHostAudit) {
                isChanged = true;
                formValues.configurations[index].hostAuditResult = undefined;
              }
            }

            // Clear tagValues when tagKey changes
            if (
              !configs.projectList ||
              isString(configs.projectList)
            ) {
              return;
            }
            forEach(configs.projectList, (object, i) => {
              if (
                object &&
                object.tagKey &&
                Object.keys(object).length === 1
              ) {
                isChanged = true;
                formValues.configurations[index].projectList[i].tagValues =
                  undefined;
              }
            });
          });
          if (isChanged) {
            form.setFieldsValue(formValues);
          }
        }
      },
      [form]
    );

    return (
      <GenericMappingForm
        title="Map Tenable Vulnerability Management Assets"
        ref={ref}
        form={form}
        getExtraFieldsData={getExtraFieldsData}
        parseExtraFieldsDataFromResponse={parseExtraFieldsDataFromResponse}
        extraFields={extraFields}
        formDefaultValues={initialValues}
        onValuesChange={onValuesChange}
        newRowDefaultValue={initialValues[0]}
        {...props}
      />
    );
  }
);

export default TenableVulnerabilityManagementMappingFormV2;